import { useEffect, useRef, useState } from "react";
import { COUNTERPARTY_TIN_GRANT_HEADER } from "@common/constants/counterpartyTin";
import { CounterpartyTinGrantAction } from "@common/types/counterpartyTin";
import {
    type CounterpartyTinIds,
    mintTinGrant,
} from "@api/counterpartyTin.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { loadVgsShow } from "@lib/vgs/loadVgsShow";
import { VGS_TIN_REVEAL_PATH, type VgsConfig } from "@lib/vgs/vgsConfig";
import type { VgsShowFrame, VgsShowFrameStyles } from "@lib/vgs/vgsShow.types";

export const TIN_REVEAL_DURATION_MS = 30_000;
const REVEAL_FAILED_MESSAGE = "Failed to reveal tax ID";

export type TinRevealStatus =
    | "hidden"
    | "revealing"
    | "revealed"
    | "stepUpRequired";

interface ActiveReveal {
    frame: VgsShowFrame | null;
    hideTimer: ReturnType<typeof setTimeout> | null;
}

function disposeReveal(activeReveal: ActiveReveal) {
    if (activeReveal.hideTimer !== null) {
        clearTimeout(activeReveal.hideTimer);
        activeReveal.hideTimer = null;
    }
    activeReveal.frame?.unmount();
    activeReveal.frame = null;
}

function frameStylesFrom(container: HTMLElement): VgsShowFrameStyles {
    const { fontFamily, fontSize, color } = getComputedStyle(container);
    // The container is still hidden when the frame renders, so measure the field it fills.
    const fieldHeight = container.parentElement?.clientHeight ?? 0;
    return {
        fontFamily,
        fontSize,
        color,
        ...(fieldHeight > 0 && { lineHeight: `${fieldHeight}px` }),
    };
}

export function useVgsShowReveal({
    ids,
    config,
}: {
    ids: CounterpartyTinIds;
    config: VgsConfig;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const activeRevealRef = useRef<ActiveReveal>({
        frame: null,
        hideTimer: null,
    });
    const [status, setStatus] = useState<TinRevealStatus>("hidden");
    const toaster = useToaster();

    const hide = () => {
        disposeReveal(activeRevealRef.current);
        setStatus("hidden");
    };

    const failReveal = (message: string | null) => {
        hide();
        toaster.error(message ?? REVEAL_FAILED_MESSAGE);
    };

    const reveal = async () => {
        disposeReveal(activeRevealRef.current);
        setStatus("revealing");

        const grantResult = await mintTinGrant({
            ids,
            action: CounterpartyTinGrantAction.REVEAL,
        });
        if (grantResult.status === "stepUpRequired") {
            setStatus("stepUpRequired");
            return;
        }
        if (grantResult.status === "failed") {
            failReveal(grantResult.message);
            return;
        }

        const show = await loadVgsShow(config).catch(() => null);
        if (!show) {
            failReveal(null);
            return;
        }
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const showInstance = show
            .create(config.vaultId, () => undefined)
            .setEnvironment(config.environment);
        if (config.cname) {
            showInstance.setCname(config.cname);
        }
        const frame = showInstance.request({
            name: "tin",
            method: "POST",
            path: VGS_TIN_REVEAL_PATH,
            headers: { [COUNTERPARTY_TIN_GRANT_HEADER]: grantResult.grant },
            jsonPathSelector: "token",
            htmlWrapper: "text",
        });
        frame.on("requestFail", () => failReveal(null));
        frame.on("revealFail", () => failReveal(null));
        frame.render(container, frameStylesFrom(container));

        activeRevealRef.current.frame = frame;
        activeRevealRef.current.hideTimer = setTimeout(
            hide,
            TIN_REVEAL_DURATION_MS,
        );
        setStatus("revealed");
    };

    useEffect(function unmountRevealedTinOnUnmount() {
        const activeReveal = activeRevealRef.current;
        return () => disposeReveal(activeReveal);
    }, []);

    return { containerRef, status, reveal, hide };
}
